import {
  AmountDimension,
  BooleanDimension,
  ExclusionDimension,
  NumbersEnumDimension,
  StringsWithDataEnumDimension,
} from '@orus.eu/dimensions'

import { MrphQuoteV2Dimension } from './mrph-quote-dimension.ts'

export * from './mrph-activity-domain.ts'
export * from './mrph-code-departement-dimension.ts'
export * from './mrph-guarantee-text.ts'
export * from './mrph-offer-model.ts'
export * from './mrph-options-dimension.ts'
export * from './mrph-pe-configuration-dimension.ts'
export * from './mrph-product-version-dimension.ts'
export * from './mrph-quote-dimension.ts'

export const mrphAmenagementLimitDimension = new AmountDimension({
  name: 'mrphAmenagementLimit',
  displayKeys: { name: 'dimension_name_mrph_amenagement_limit' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrphItAssetsValueDimension = new AmountDimension({
  name: 'mrphItAssetsValue',
  displayKeys: {
    name: 'dimension_name_mrph_it_assets_value',
    placeholder: 'dimension_placeholder_mrph_it_assets_value',
  },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrphFormulaDimension = new StringsWithDataEnumDimension({
  name: 'mrphFormula',
  displayKeys: { name: 'dimension_name_mrph_formula' },
  entries: [
    ['Basique', { label: 'Basique' }],
    ['Confort', { label: 'Confort' }],
    ['Premium', { label: 'Premium' }],
  ],
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrphAssetsPctLimitOneDimension = new AmountDimension({
  name: 'mrphAssetsPctLimitOne',
  displayKeys: { name: 'dimension_name_mrph_assets_pct_limit_one', hint: 'dimension_hint_mrph_assets_pct_limit_one' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrphAssetsPctLimitTwoDimension = new AmountDimension({
  name: 'mrphAssetsPctLimitTwo',
  displayKeys: { name: 'dimension_name_mrph_assets_pct_limit_two', hint: 'dimension_hint_mrph_assets_pct_limit_two' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrphRevenuePctLimitDimension = new AmountDimension({
  name: 'mrphRevenuePctLimit',
  displayKeys: { name: 'dimension_name_mrph_revenue_pct_limit', hint: 'dimension_hint_mrph_revenue_pct_limit' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrphFixedLimitOneDimension = new AmountDimension({
  name: 'mrphFixedLimitOne',
  displayKeys: { name: 'dimension_name_mrph_fixed_limit_one', hint: 'dimension_hint_mrph_fixed_limit_one' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrphFixedLimitTwoDimension = new AmountDimension({
  name: 'mrphFixedLimitTwo',
  displayKeys: { name: 'dimension_name_mrph_fixed_limit_two', hint: 'dimension_hint_mrph_fixed_limit_two' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrphLciDimension = new AmountDimension({
  name: 'mrphLci',
  displayKeys: { name: 'dimension_name_mrph_lci' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrphMonumentDimension = new BooleanDimension({
  name: 'mrphMonument',
  displayKeys: { name: 'dimension_name_mrph_monument' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrphDeductibleCurrentValues = [300, 500, 750, 1000, 1500, 2000, 2500, 3000, 5000] as const
/** The values that have been used in the past, but is no longer accepted for new subscriptions */
export const mrphDeductibleDeprecatedValues = [10000] as const
export const mrphDeductibleSupportedValues = [
  ...mrphDeductibleCurrentValues,
  ...mrphDeductibleDeprecatedValues,
] as const
export type MrphDeductible = (typeof mrphDeductibleSupportedValues)[number]
export function isMrphDeductible(value: unknown): value is MrphDeductible {
  return typeof value === 'number' && (mrphDeductibleSupportedValues as readonly number[]).includes(value)
}

export const mrphDeductibleDimension = new NumbersEnumDimension({
  name: 'mrphDeductible',
  displayKeys: { name: 'dimension_name_mrph_deductible' },
  values: mrphDeductibleCurrentValues,
  supportedValues: mrphDeductibleSupportedValues,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
  valuesAreAmount: true,
} as const)

export const mrphDeductibleDaysDimension = new NumbersEnumDimension({
  name: 'mrphDeductibleDays',
  displayKeys: { name: 'dimension_name_mrph_deductible_days' },
  values: [0, 3, 5],
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
  valuesAreAmount: false,
} as const)

export const mrphIndemnityDurationMonthsDimension = new NumbersEnumDimension({
  name: 'mrphIndemnityDurationMonths',
  displayKeys: { name: 'dimension_name_mrph_indemnity_duration_months' },
  values: [12, 18, 24, 36],
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
  valuesAreAmount: false,
} as const)

export const mrphOccupationStatusDimension = new StringsWithDataEnumDimension({
  name: 'mrphOccupationStatus',
  displayKeys: {
    name: 'dimension_name_mrph_occupation_status',
    placeholder: 'dimension_placeholder_mrph_occupation_status',
  },
  entries: [
    ['LOCATAIRE', { label: 'Locataire' }],
    [
      'LOCATAIRE AGISSANT POUR LE COMPTE DU PROPRIETAIRE',
      { label: 'Locataire agissant pour le compte du propriétaire' },
    ],
    ['OCCUPANT A TITRE GRATUIT', { label: 'Occupant à titre gratuit' }],
    ['PROPRIETAIRE', { label: 'Propriétaire' }],
    ['SOUS LOCATAIRE', { label: 'Sous-locataire' }],
  ],
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrphHardBuildingDimension = new BooleanDimension({
  name: 'mrphHardBuilding',
  displayKeys: { name: 'dimension_name_mrph_hard_building' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrphOwnerDimension = new BooleanDimension({
  name: 'mrphOwner',
  displayKeys: { name: 'dimension_name_mrph_owner' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrphLocataireAgissantProprietaireDimension = new BooleanDimension({
  name: 'mrphLocataireAgissantProprietaire',
  displayKeys: { name: 'dimension_name_mrph_locataire_agissant_proprietaire' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

type MrphClaimsThreeYearsState = { mrphClaimsThreeYears?: '0' | '1' | '2' | '3+' }

export const mrphAssetsClaimsThreeYearsDimension = new StringsWithDataEnumDimension({
  name: 'mrphAssetsClaimsThreeYears',
  initStrategy: {
    oldNames: ['mrphClaimsThreeYears'],
    computeValue: (state: MrphClaimsThreeYearsState) => state.mrphClaimsThreeYears,
  },
  displayKeys: {
    name: 'dimension_name_mrph_assets_claims_three_years',
    hint: 'dimension_hint_mrph_assets_claims_three_years',
  },
  entries: [
    ['0', { label: '0' }],
    ['1', { label: '1' }],
    ['2', { label: '2' }],
    ['3+', { label: '3+' }],
  ],
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrphOptionPeDimension = new StringsWithDataEnumDimension({
  name: 'mrphOptionPe',
  displayKeys: { name: 'dimension_name_mrph_option_pe' },
  entries: [
    ['Non', { label: 'Non' }],
    ['Standard', { label: 'Standard' }],
    ['Avantage', { label: 'Avantage' }],
  ],
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrphOptionPeEntries = mrphOptionPeDimension.values

// 'Non' was used when mrphOptionPeDimension was a computed dimension, we don't need it anymore.
export const mrphOptionPeValues: string[] = mrphOptionPeEntries.filter((entry) => entry !== 'Non')

export type MrphOptionPeEntries = (typeof mrphOptionPeDimension.values)[number]

export const mrphOptionPeLabels: Record<MrphOptionPeEntries, string> = Object.fromEntries(
  mrphOptionPeDimension.entries
    .filter(([key, _]) => key !== 'Non')
    .map(([key, def]) => {
      return [key, def.label]
    }),
) as Record<MrphOptionPeEntries, string>

export const mrphOptionPeActiveDimension = new BooleanDimension({
  name: 'mrphOptionPeActive',
  displayKeys: { name: 'dimension_name_mrph_option_pe_active' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrphAvantageDimension = new BooleanDimension({
  name: 'mrphAvantage',
  displayKeys: { name: 'dimension_name_mrph_avantage' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrphOptionPeAvantageAvailableDimension = new BooleanDimension({
  name: 'mrphOptionPeAvantageAvailable',
  displayKeys: { name: 'dimension_name_mrph_option_pe_avantage_available' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
})

export const mrphOptionRcalDimension = new BooleanDimension({
  name: 'mrphOptionRcal',
  displayKeys: { name: 'dimension_name_mrph_option_rcal' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const mrphOptionRceDimension = new BooleanDimension({
  name: 'mrphOptionRce',
  displayKeys: { name: 'dimension_name_mrph_option_rce' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const mrphOptionAssistanceDimension = new BooleanDimension({
  name: 'mrphOptionAssistance',
  displayKeys: { name: 'dimension_name_mrph_option_assistance' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const mrphOptionPjDimension = new BooleanDimension({
  name: 'mrphOptionPj',
  displayKeys: { name: 'dimension_name_mrph_option_pj' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const mrphYearlyBasePremiumDimension = new AmountDimension({
  name: 'mrphYearlyBasePremium',
  displayKeys: { name: 'dimension_name_mrph_yearly_base_premium' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const mrphYearlyTaxesDimension = new AmountDimension({
  name: 'mrphYearlyTaxes',
  displayKeys: { name: 'dimension_name_mrph_yearly_taxes' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const mrphYearlyTotalPremiumDimension = new AmountDimension({
  name: 'mrphYearlyTotalPremium',
  displayKeys: { name: 'dimension_name_mrph_yearly_total_premium' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: true,
  inputLocking: undefined,
} as const)
export const mrphTerrorismTaxDimension = new AmountDimension({
  name: 'mrphTerrorismTax',
  displayKeys: { name: 'dimension_name_mrph_terrorism_tax' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrphNaturalDisasterPremiumDimension = new AmountDimension({
  name: 'mrphNaturalDisasterPremium',
  displayKeys: { name: 'dimension_name_mrph_natural_disaster_premium' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrphAssistancePremiumDimension = new AmountDimension({
  name: 'mrphAssistancePremium',
  displayKeys: { name: 'dimension_name_mrph_assistance_premium' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrphOptionFreedomAdvantageDimension = new BooleanDimension({
  name: 'mrphOptionFreedomAdvantage',
  displayKeys: { name: 'dimension_name_mrph_option_freedom_advantage' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrphQuoteV2Dimension = new MrphQuoteV2Dimension({
  name: 'mrphQuoteV2',
  displayKeys: { name: 'dimension_name_mrph_quote_v2' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrphExclusionsDimension = new ExclusionDimension({
  name: 'mrphExclusions',
  displayKeys: { name: 'dimension_name_mrph_exclusions' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: false },
  includeInTrackingMessages: false,
  inputLocking: undefined,
})
